import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@treatwell/ui';
import { Button, ButtonColour, ButtonSize } from 'js/components/Button';
import { VenueListingCriteria } from 'js/model/rainbow/browse-page/VenueListingCriteria';
import { trackStructuredEvent } from 'js/helpers/google-tag-manager';
import { changeBrowseDataContext, AddLocation } from '../BrowseData';
import { ContextualButtonSelectedIcon } from './ContextualButtonSelectedIcon';
import { ContextualButtonUnselectedIcon } from './ContextualButtonUnselectedIcon';
import styles from './ContextualFilterButton.module.css';

interface Props {
  label: string;
  id: number;
  isRelatedTreatment: boolean;
  onFilterApplied: () => void;
}

export const ContextualFilterButton = (props: Props) => {
  const [isSelected, setIsSelected] = useState(false);
  const { notifyError } = useNotify();
  const { t } = useTranslation();
  const changeBrowseData = useContext(changeBrowseDataContext);

  const trackEvent = () => {
    if (props.isRelatedTreatment) {
      trackStructuredEvent({
        category: 'browse',
        property: 'related_treatment_experiment',
        action: 'click',
        label: props.id,
      });
    } else {
      trackStructuredEvent({
        category: 'browse_contextual_filters',
        property: 'treatment_filter',
        action: 'select',
        label: props.label,
        value: props.id,
      });
    }
  };

  const onClick = async () => {
    const criteria: VenueListingCriteria = {
      treatmentCategoryIds: [props.id],
    };
    setIsSelected(true);
    trackEvent();
    try {
      await changeBrowseData(
        criteria,
        undefined,
        AddLocation.CurrentPageWithRadius
      );

      props.onFilterApplied();
    } catch {
      notifyError(t('referral.otpInputModal.error.generic'));
    } finally {
      setIsSelected(false);
    }
  };

  const icon = (classname: string) => {
    if (isSelected) {
      return <ContextualButtonSelectedIcon classname={classname} />;
    }
    return <ContextualButtonUnselectedIcon classname={classname} />;
  };

  const buttonClassnames = isSelected
    ? ButtonColour.OnBackgroundSelected
    : ButtonColour.OnBackground;

  return (
    <div className={styles.contextualButton} data-cy="ContextualFilterButton">
      <Button
        label={props.label}
        size={ButtonSize.OnBackground}
        colour={buttonClassnames}
        icon={icon}
        mobileHideLabel={false}
        onClick={onClick}
      />
    </div>
  );
};
